import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Modal, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { connect, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import api, {
  firstLoginPromoFallback,
  standardSixMonthCheckoutProgramId,
} from "../constants/config";
import SnackBar from "../atoms/snackBar";
import { setUserData } from "../redux/action/user";
import { isSubscriptionActive } from "../common";

const DEFAULT_ENDPOINTS = {
  getOffer: "me/first-login-offer",
  dismissOffer: "me/first-login-offer/dismiss",
  createPayCheckout: "create-pay-checkout",
};

function normalizeEnabledFlag(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

function decodeHtmlEntities(input) {
  const str = input == null ? "" : String(input);
  if (!str) return "";
  // Browser-safe decode for strings like "&lt;div&gt;...&lt;/div&gt;"
  const textarea = document.createElement("textarea");
  textarea.innerHTML = str;
  return textarea.value;
}

function messageFromApiError(err) {
  if (err == null) return null;
  if (typeof err === "string") return err;
  if (typeof err === "object") {
    if (typeof err.message === "string" && err.message) return err.message;
    if (typeof err.error === "string" && err.error) return err.error;
    if (err.error && typeof err.error.message === "string") return err.error.message;
    if (typeof err.msg === "string" && err.msg) return err.msg;
  }
  return null;
}

const FirstLoginUpgradeModal = (props) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalContentHtml, setModalContentHtml] = useState("");
  const [modalButtonLabel, setModalButtonLabel] = useState("");
  const [programId, setProgramId] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const token = localStorage.getItem("userKey");
  const isSubscribed = isSubscriptionActive(props?.user);

  const endpoints = useMemo(() => DEFAULT_ENDPOINTS, []);

  const completedSurveyCount = useMemo(() => {
    const list = props?.user?.surveyIdList;
    if (!Array.isArray(list)) return 0;
    return list.filter((s) => s?.s_IsSurveyCompleted === 1).length;
  }, [props?.user?.surveyIdList]);

  const isOnResultScreen = useMemo(() => {
    const p = location?.pathname || "";
    return p === "/result" || p.startsWith("/result/");
  }, [location?.pathname]);

  // Once opened, keep open until user action (dismiss/upgrade).
  // This avoids dev StrictMode double-effect or any re-fetch from auto-closing.
  const holdOpenUntilUserActionRef = useRef(false);

  const safeClose = () => {
    holdOpenUntilUserActionRef.current = false;
    setOpen(false);
    dispatch(
      setUserData({
        isLoggedinModal: false,
      })
    );
  };

  const fetchOffer = async () => {
    if (!token || isSubscribed) return;
    // Only show after at least one completed assessment, and only on Result screen.
    if (!isOnResultScreen || completedSurveyCount < 1) return;
    // Prevent re-opening on subsequent visits once shown.
    if (localStorage.getItem("firstLoginOfferShownAfterSurvey") === "true") return;

    setIsLoading(true);
    if (!holdOpenUntilUserActionRef.current) {
      setModalTitle("");
      setModalContentHtml("");
      setModalButtonLabel("");
      setProgramId(null);
      setPromoCode("");
    }
    try {
      const res = await api.get(endpoints.getOffer);
      const data = res?.data?.data ?? res?.data ?? {};

      const enabled = normalizeEnabledFlag(data.enabled ?? data.shouldShow ?? data.showOffer);
      const title = (data.modal_title ?? data.title ?? data.header ?? "").toString();
      const content = decodeHtmlEntities(data.modal_content ?? data.content ?? "");
      const coupon = (
        data.coupon ??
        data.couponCode ??
        data.promoCode ??
        data.code ??
        data.promotionCode ??
        ""
      )
        .toString()
        .trim();
      const btnLabel = (data.modal_button_label ?? data.buttonLabel ?? data.ctaLabel ?? "")
        .toString();
      const rawProgramId = data.program_id ?? data.programId ?? data.ID ?? data.id;
      const parsedProgramId =
        rawProgramId == null || String(rawProgramId).trim() === ""
          ? null
          : Number(rawProgramId);
      const finalProgramId = Number.isFinite(parsedProgramId)
        ? parsedProgramId
        : null;

      const eligible =
        data.eligible === undefined ? true : normalizeEnabledFlag(data.eligible);
      const dismissed =
        data.dismissed === undefined ? false : normalizeEnabledFlag(data.dismissed);
      const hasActiveSubscription =
        data.hasActiveSubscription === undefined
          ? false
          : normalizeEnabledFlag(data.hasActiveSubscription);

      const shouldShow =
        enabled && eligible && !dismissed && hasActiveSubscription === false;

      // If we've already opened, keep it open until user action.
      if (holdOpenUntilUserActionRef.current) {
        setOpen(true);
        setModalTitle(title);
        setModalContentHtml(content);
        setModalButtonLabel(btnLabel);
        setProgramId(finalProgramId);
        setPromoCode(coupon || firstLoginPromoFallback || "");
        return;
      }

      if (shouldShow) {
        holdOpenUntilUserActionRef.current = true;
        setOpen(true);
        setModalTitle(title);
        setModalContentHtml(content);
        setModalButtonLabel(btnLabel);
        setProgramId(finalProgramId);
        setPromoCode(coupon || firstLoginPromoFallback || "");
        localStorage.setItem("firstLoginOfferShownAfterSurvey", "true");
      } else if (!holdOpenUntilUserActionRef.current) {
        setOpen(false);
        dispatch(setUserData({ isLoggedinModal: false }));
      }
    } catch (e) {
      if (!holdOpenUntilUserActionRef.current) {
        setOpen(false);
        dispatch(setUserData({ isLoggedinModal: false }));
      }
      const msg = messageFromApiError(e) || "Could not load first-login offer.";
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isSubscribed) {
      setOpen(false);
      dispatch(setUserData({ isLoggedinModal: false }));
    }
  }, [isSubscribed, dispatch]);

  useEffect(() => {
    if (!token || isSubscribed) return;
    if (!isOnResultScreen || completedSurveyCount < 1) return;
    const t = window.setTimeout(() => {
      fetchOffer();
    }, 3000);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isSubscribed, isOnResultScreen, completedSurveyCount]);

  const dismissOffer = async () => {
    try {
      await api.post(endpoints.dismissOffer);
    } catch {
      // Ignore; FE will still close.
    } finally {
      localStorage.setItem("firstLoginOfferShownAfterSurvey", "true");
      safeClose();
    }
  };

  const upgradeToStandard6mo = async () => {
    setCheckoutLoading(true);
    try {
      const couponFromModal = promoCode && String(promoCode).trim();
      const couponFromStorage = localStorage.getItem("couponCode");
      const coponCode =
        couponFromModal || (couponFromStorage && couponFromStorage.trim()) || null;

      const payload = {
        ID: programId ?? standardSixMonthCheckoutProgramId,
        coponCode,
      };

      const res = await api.post(endpoints.createPayCheckout, payload);
      const url =
        res?.data?.data?.url ??
        res?.data?.url ??
        res?.data?.data?.sessionUrl ??
        res?.data?.sessionUrl;
      if (url) {
        localStorage.setItem("firstLoginOfferShownAfterSurvey", "true");
        safeClose();
        window.location.href = url;
      } else {
        setSnack({
          open: true,
          message: "Checkout URL missing from server response.",
          severity: "error",
        });
      }
    } catch (err) {
      const msg =
        messageFromApiError(err) ||
        "Could not start checkout. Check program ID and create-pay-checkout on the server.";
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <Fragment>
      <Modal
        open={open}
        onClose={() => {}}
        disableEscapeKeyDown
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      >
        <Box sx={offerModalStyle}>
          {/* Top bar */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1.25,
              background: "#b99a57",
              color: "#0b241e",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              fontSize: "0.75rem",
              fontWeight: 700,
            }}
          >
            <Box
              sx={{
                position: "relative",
                color: "#1a1a14",
                textAlign: "center",
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: ".12em",
                padding: "12px 36px 12px 16px",
                width: "100%",
              }}
            >
              {modalTitle || "One-time offer · disappears when you leave this screen"}
            </Box>
            <CloseIcon
              onClick={dismissOffer}
              sx={{ cursor: "pointer", fontSize: "18px" }}
            />
          </Box>

          {/* Body */}
          <Box sx={{ px: 3, pt: 3, pb: 2.5, background: "#0f2a23", color: "#f3efe2" }}>
            <Box
              sx={{ color: "rgba(243, 239, 226, 0.9)", mb: 3 }}
              dangerouslySetInnerHTML={{
                __html: modalContentHtml || "<p></p>",
              }}
            />

            {/* CTA */}
            <Button
              fullWidth
              variant="contained"
              onClick={upgradeToStandard6mo}
              disabled={isLoading || checkoutLoading}
              sx={{
                background: "#b99a57",
                color: "#0b241e",
                fontWeight: 800,
                textTransform: "none",
                py: 1.4,
                borderRadius: "6px",
                "&:hover": { background: "#a98a4a" },
              }}
            >
              {checkoutLoading ? "Opening checkout…" : (modalButtonLabel || "Claim now")}
            </Button>

            <Typography
              sx={{
                mt: 1.25,
                fontSize: "0.85rem",
                textAlign: "center",
                color: "rgba(243, 239, 226, 0.7)",
              }}
            >
              This offer disappears the moment you leave this screen.
            </Typography>

            <Button
              variant="text"
              onClick={dismissOffer}
              sx={{
                mt: 0.75,
                width: "100%",
                textTransform: "none",
                color: "rgba(201, 173, 106, 0.95)",
                textDecoration: "underline",
                "&:hover": { textDecoration: "underline", background: "transparent" },
              }}
            >
              No thanks
            </Button>
          </Box>
        </Box>
      </Modal>

      <SnackBar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        handleCloseSnackBar={() => setSnack({ open: false, message: "", severity: "success" })}
      />
    </Fragment>
  );
};

export default connect(({ user }) => ({ user }))(FirstLoginUpgradeModal);

const offerModalStyle = {
  width: "min(520px, 94vw)",
  maxHeight: "92vh",
  overflow: "auto",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  boxShadow: 24,
  borderRadius: "10px",
  border: "1px solid rgba(201, 173, 106, 0.35)",
};

